// SkuCatalog — global SKU catalog admin at /admin/sku-catalog.
// Read + inline add / edit / deprecate / reactivate. Calls plex_sku_catalog
// CRUD endpoints via SkuCatalogStore.

import {useEffect, useState} from "react";
import {inject, observer} from "mobx-react";

const SKU_KEY_RE = /^[a-z][a-z0-9-]{2,30}$/;
const STATUSES = ["active", "internal-preview", "deprecated"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const statusChip = (status) => {
  const styles = {
    active:             { bg: "var(--ok-bg, #e6f6ec)",   fg: "var(--ok, #1b6f3d)" },
    deprecated:         { bg: "var(--warn-bg, #fcebe2)", fg: "var(--warn, #a85a13)" },
    "internal-preview": { bg: "var(--info-bg, #e6effe)", fg: "var(--info, #1f4fab)" },
  };
  const s = styles[status] || { bg: "var(--bg-2, #f3f3f3)", fg: "var(--muted)" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 12,
      fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase",
      background: s.bg, color: s.fg,
    }}>
      {status}
    </span>
  );
};

const inputStyle = {
  height: 32, padding: "0 10px", borderRadius: 6,
  border: "1px solid var(--line)", fontFamily: "inherit", fontSize: 13,
  width: "100%",
};

const labelStyle = {display: "flex", flexDirection: "column", gap: 4};
const labelTextStyle = {
  fontSize: 11, color: "var(--muted)",
  textTransform: "uppercase", letterSpacing: ".06em",
};

// ─── Add SKU form ─────────────────────────────────────────────────────────

const AddSkuForm = ({onSubmit, onCancel, isPending}) => {
  const [skuKey, setSkuKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!SKU_KEY_RE.test(skuKey)) {
      setErr("sku_key must match ^[a-z][a-z0-9-]{2,30}$ (kebab-case, 3-31 chars)");
      return;
    }
    if (!displayName.trim()) { setErr("display_name is required"); return; }
    if (!description.trim()) { setErr("description is required"); return; }
    try {
      await onSubmit({
        sku_key: skuKey.trim(),
        display_name: displayName.trim(),
        description: description.trim(),
        status,
      });
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  return (
    <div className="stone-card--subtle" style={{
      padding: 16, marginBottom: 20,
      display: "grid", gridTemplateColumns: "1fr 1.4fr 2fr 1fr auto auto", gap: 10,
      alignItems: "end",
    }}>
      <label style={labelStyle}>
        <span style={labelTextStyle}>SKU key</span>
        <input
          style={{...inputStyle, fontFamily: "var(--mono)"}}
          value={skuKey}
          onChange={(e) => setSkuKey(e.target.value)}
          placeholder="new-feature"
          autoFocus
        />
      </label>
      <label style={labelStyle}>
        <span style={labelTextStyle}>Display name</span>
        <input
          style={inputStyle}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="New Feature"
        />
      </label>
      <label style={labelStyle}>
        <span style={labelTextStyle}>Description</span>
        <input
          style={inputStyle}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short customer-facing description"
        />
      </label>
      <label style={labelStyle}>
        <span style={labelTextStyle}>Status</span>
        <select
          style={inputStyle}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <button className="btn primary button-primary" onClick={submit} disabled={isPending}>
        {isPending ? "Adding…" : "Add"}
      </button>
      <button className="btn ghost button-ghost" onClick={onCancel} disabled={isPending}>
        Cancel
      </button>
      {err && (
        <div className="err" style={{gridColumn: "1 / -1", marginBottom: 0}}>
          {err}
        </div>
      )}
    </div>
  );
};

// ─── Edit row (inline) ────────────────────────────────────────────────────

const EditRow = ({sku, onSubmit, onCancel, isPending}) => {
  const [displayName, setDisplayName] = useState(sku.display_name || "");
  const [description, setDescription] = useState(sku.description || "");
  const [status, setStatus] = useState(sku.status || "active");
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    // Only send fields that actually changed — matches the backend's
    // "partial update with at least one field" contract.
    const fields = {};
    if (displayName !== (sku.display_name || "")) fields.display_name = displayName;
    if (description !== (sku.description || "")) fields.description = description;
    if (status !== sku.status) fields.status = status;
    if (Object.keys(fields).length === 0) {
      setErr("No changes to save");
      return;
    }
    try {
      await onSubmit(sku.sku_key, fields);
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  return (
    <tr>
      <td colSpan={7} style={{
        padding: "14px 14px", borderBottom: "1px solid var(--line-2)",
        background: "var(--bg)",
      }}>
        <div className="stone-card--subtle" style={{
          display: "grid", gridTemplateColumns: "auto 1.4fr 2fr 1fr auto auto",
          gap: 10, alignItems: "end",
        }}>
          <div style={{...labelStyle, alignSelf: "end", paddingBottom: 8}}>
            <span style={labelTextStyle}>SKU key</span>
            <code style={{fontSize: 13, color: "var(--ink)"}}>{sku.sku_key}</code>
          </div>
          <label style={labelStyle}>
            <span style={labelTextStyle}>Display name</span>
            <input style={inputStyle} value={displayName}
                   onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label style={labelStyle}>
            <span style={labelTextStyle}>Description</span>
            <input style={inputStyle} value={description}
                   onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label style={labelStyle}>
            <span style={labelTextStyle}>Status</span>
            <select style={inputStyle} value={status}
                    onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <button className="btn primary button-primary" onClick={submit} disabled={isPending}>
            {isPending ? "Saving…" : "Save"}
          </button>
          <button className="btn ghost button-ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </button>
          {err && (
            <div className="err" style={{gridColumn: "1 / -1", marginBottom: 0}}>
              {err}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────

const SkuCatalog = ({skuCatalogStore}) => {
  useEffect(() => { skuCatalogStore.load(); }, [skuCatalogStore]);
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [rowErr, setRowErr] = useState(null);  // surface deprecate/reactivate errors

  const skus = (showDeprecated ? skuCatalogStore.skus : skuCatalogStore.activeSkus)
    .slice()
    .sort((a, b) => a.sku_key.localeCompare(b.sku_key));

  const counts = {
    active: skuCatalogStore.activeSkus.length,
    deprecated: skuCatalogStore.deprecatedSkus.length,
  };

  const handleAdd = async (payload) => {
    await skuCatalogStore.add(payload);
    setAdding(false);
  };

  const handleEdit = async (skuKey, fields) => {
    await skuCatalogStore.update(skuKey, fields);
    setEditingKey(null);
  };

  const handleDeprecate = async (sku) => {
    if (!confirm(`Deprecate ${sku.sku_key}? Existing grants stay active; this only hides it from the grant form.`)) return;
    setRowErr(null);
    try { await skuCatalogStore.deprecate(sku.sku_key); }
    catch (e) { setRowErr(`Deprecate failed: ${e?.message || e}`); }
  };

  const handleReactivate = async (sku) => {
    setRowErr(null);
    try { await skuCatalogStore.update(sku.sku_key, {status: "active"}); }
    catch (e) { setRowErr(`Reactivate failed: ${e?.message || e}`); }
  };

  return (
    <>
      <div className="page-hero">
        <div>
          <div className="adminEnt-eyebrow section-eyebrow">Catalog</div>
          <h1 className="serif hero-display">SKU Catalog</h1>
          <p className="lede">
            Global catalog of features that can be entitled. Plex employees and
            members of the <code>sku-admin</code> group can add, edit, and
            deprecate SKUs here.
          </p>
        </div>
        <div className="page-hero-actions" style={{display: "flex", gap: 12, alignItems: "center"}}>
          <label style={{display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--muted)"}}>
            <input
              type="checkbox"
              checked={showDeprecated}
              onChange={(e) => setShowDeprecated(e.target.checked)}
            />
            Show deprecated
          </label>
          <button
            className="btn button-ghost"
            onClick={() => skuCatalogStore.load()}
            disabled={skuCatalogStore.loading}
          >
            {skuCatalogStore.loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            className="btn primary button-primary"
            onClick={() => { setAdding(true); setEditingKey(null); }}
            disabled={adding}
          >
            + Add SKU
          </button>
        </div>
      </div>

      <div style={{
        display: "flex", gap: 16, marginBottom: 18,
        fontSize: 13, color: "var(--muted)",
      }}>
        <span><strong style={{color: "var(--ink)"}}>{counts.active}</strong> active</span>
        <span><strong style={{color: "var(--ink)"}}>{counts.deprecated}</strong> deprecated</span>
        {skuCatalogStore.lastFetchedAt && (
          <span>· last loaded {formatDate(skuCatalogStore.lastFetchedAt)}</span>
        )}
      </div>

      {skuCatalogStore.error && (
        <div className="err">Error: {skuCatalogStore.error}</div>
      )}
      {rowErr && <div className="err">{rowErr}</div>}

      {adding && (
        <AddSkuForm
          onSubmit={handleAdd}
          onCancel={() => setAdding(false)}
          isPending={false}
        />
      )}

      {skus.length === 0 ? (
        <div style={{
          padding: "30px 20px", border: "1px dashed var(--line)", borderRadius: 8,
          color: "var(--muted)", textAlign: "center",
        }}>
          {skuCatalogStore.loading
            ? "Loading…"
            : showDeprecated
              ? "Catalog is empty."
              : "No active SKUs. Toggle “Show deprecated” to see deprecated entries."}
        </div>
      ) : (
        <div className="stone-card" style={{overflow: "hidden"}}>
          <table style={{width: "100%", borderCollapse: "collapse", fontSize: 13}}>
            <thead>
              <tr style={{
                textAlign: "left", color: "var(--muted)",
                fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em",
                background: "var(--bg, #fafafa)",
              }}>
                <th style={{padding: "10px 14px", borderBottom: "1px solid var(--line)"}}>SKU key</th>
                <th style={{padding: "10px 14px", borderBottom: "1px solid var(--line)"}}>Display name</th>
                <th style={{padding: "10px 14px", borderBottom: "1px solid var(--line)"}}>Description</th>
                <th style={{padding: "10px 14px", borderBottom: "1px solid var(--line)"}}>Status</th>
                <th style={{padding: "10px 14px", borderBottom: "1px solid var(--line)"}}>Last modified</th>
                <th style={{padding: "10px 14px", borderBottom: "1px solid var(--line)"}}>Modified by</th>
                <th style={{padding: "10px 14px", borderBottom: "1px solid var(--line)", textAlign: "right"}}></th>
              </tr>
            </thead>
            <tbody>
              {skus.map((sku) => {
                const isEditing = editingKey === sku.sku_key;
                const isPending = skuCatalogStore.isPending(sku.sku_key);

                if (isEditing) {
                  return (
                    <EditRow
                      key={sku.sku_key}
                      sku={sku}
                      onSubmit={handleEdit}
                      onCancel={() => setEditingKey(null)}
                      isPending={isPending}
                    />
                  );
                }

                return (
                  <tr key={sku.sku_key}>
                    <td style={{padding: "12px 14px", borderBottom: "1px solid var(--line-2)", fontFamily: "var(--mono)"}}>
                      {sku.sku_key}
                    </td>
                    <td style={{padding: "12px 14px", borderBottom: "1px solid var(--line-2)", fontWeight: 500}}>
                      {sku.display_name}
                    </td>
                    <td style={{padding: "12px 14px", borderBottom: "1px solid var(--line-2)", color: "var(--muted)"}}>
                      {sku.description}
                    </td>
                    <td style={{padding: "12px 14px", borderBottom: "1px solid var(--line-2)"}}>
                      {statusChip(sku.status)}
                    </td>
                    <td style={{padding: "12px 14px", borderBottom: "1px solid var(--line-2)", color: "var(--muted)"}}>
                      {formatDate(sku.last_modified_at)}
                    </td>
                    <td style={{padding: "12px 14px", borderBottom: "1px solid var(--line-2)", color: "var(--muted)"}}>
                      {sku.last_modified_by || "—"}
                    </td>
                    <td style={{padding: "12px 14px", borderBottom: "1px solid var(--line-2)", textAlign: "right", whiteSpace: "nowrap"}}>
                      <button
                        className="btn small ghost button-ghost"
                        onClick={() => { setEditingKey(sku.sku_key); setAdding(false); }}
                        disabled={isPending || editingKey !== null}
                      >
                        Edit
                      </button>
                      {sku.status === "deprecated" ? (
                        <button
                          className="btn small button-primary"
                          style={{marginLeft: 6}}
                          onClick={() => handleReactivate(sku)}
                          disabled={isPending}
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          className="btn small ghost button-ghost"
                          style={{marginLeft: 6}}
                          onClick={() => handleDeprecate(sku)}
                          disabled={isPending}
                        >
                          Deprecate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default inject("skuCatalogStore")(observer(SkuCatalog));
